import React, { Suspense, useMemo } from 'react';

import { AnalyticsContext } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { FeatureExposedAnalytics } from '../analytics/feature-exposed-analytics';
import {
	EditionAwarenessContext,
	type EditionAwarenessContextType,
	SiteInformationContext,
	useEditionAwareness,
} from '../services/edition-awareness-context';
import { getTrialOfferingName } from '../services/get-trial-offering-name';
import { isEligibleForStandardToPremium } from '../services/is-eligible-for-standard-to-premium';
import { useConfig } from '../services/use-config';
import { useSpotlight } from '../services/use-spotlight';
import type { Edition, ExperimentCohort, Product, UserRole } from '../types';

import { StandardToPremiumDialog } from './dialogs/standard-to-premium';
import { ErrorBoundary } from './error-boundary';
import { Spotlight } from './spotlight';

export type EditionAwarenessUpsellProps = {
	cloudId: string;
	product: Product;
	edition: Edition;
	userRoles?: UserRole[];
	hasDismissedButton?: boolean;
	onDismissButton?: () => void;
	navVersion?: 'nav3' | 'nav4';
};

/**
 * Edition awareness upsell is an experimental EA implementtiona that has to be exported as its own component to avoid making ccp calls which are alerting VC90 mertics
 *
 * This component deals with creating the context which is then used throughout the package to:
 * - Track analytics
 * - Know which product/edition/cloud id is being used
 * - Know when to show/hide the button
 */
function EditionAwarenessUpsellImpl({
	edition,
	product,
	cloudId,
	userRoles,
	hasDismissedButton,
	onDismissButton,
	navVersion,
}: EditionAwarenessUpsellProps) {
	const config = useConfig(product, edition);
	const { isSpotlightActive, handleSetSpotlight } = useSpotlight();

	const trialOfferingName = getTrialOfferingName(edition, config);

	const context: EditionAwarenessContextType = useMemo(() => {
		return {
			config,
			cloudId,
			product,
			edition,
			userRoles,
			hasDismissedButton,
			onDismissButton,
			isSpotlightActive,
			handleSetSpotlight,
			trialOfferingName,
		};
	}, [
		config,
		cloudId,
		product,
		edition,
		userRoles,
		hasDismissedButton,
		onDismissButton,
		isSpotlightActive,
		handleSetSpotlight,
		trialOfferingName,
	]);

	return (
		<EditionAwarenessContext.Provider value={context}>
			<AnalyticsContext data={{ navVersion }}>
				<StandardEdition />
				<Spotlight />
			</AnalyticsContext>
		</EditionAwarenessContext.Provider>
	);
}

export function EditionAwarenessUpsell(props: EditionAwarenessUpsellProps) {
	const { product, edition } = props;

	return (
		<RelayEnvironmentProvider>
			<UFOSegment name="edition-awareness-platform">
				{/* This needs to be something that is not null, if it is null then the error will
				bubble up to the parent as `ErrorBoundary` will throw an exception if it is passed `null` */}
				<ErrorBoundary product={product} edition={edition} id="edition-awareness-upsell">
					<Suspense fallback={null}>
						<EditionAwarenessUpsellImpl {...props} />
					</Suspense>
				</ErrorBoundary>
			</UFOSegment>
		</RelayEnvironmentProvider>
	);
}

function StandardEdition() {
	const context = useEditionAwareness();
	const { product, hasDismissedButton } = context;

	if (isEligibleForStandardToPremium(context) && !hasDismissedButton) {
		const experimentKey =
			product === 'confluence'
				? 'edition_awareness_standard_upsell_confluence_ab'
				: 'edition_awareness_upsell_jira_ab';
		const cohort = FeatureGates.getExperimentValue<ExperimentCohort>(
			experimentKey,
			'cohort',
			'not-enrolled',
		);

		return (
			<>
				{cohort === 'control' && (
					<FeatureExposedAnalytics flagKey={experimentKey} cohort={cohort} />
				)}
				{cohort === 'treatment' && (
					<FeatureExposedAnalytics flagKey={experimentKey} cohort={cohort}>
						<SiteInformationContext.Provider value={{ licensedUsers: 0 }}>
							<StandardToPremiumDialog />
						</SiteInformationContext.Provider>
					</FeatureExposedAnalytics>
				)}
			</>
		);
	}

	return null;
}
